import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  Chip,
  TextField,
  FormControl,
  Select,
  MenuItem,
  InputAdornment,
  IconButton,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import LayersIcon from '@mui/icons-material/Layers';
import DescriptionIcon from '@mui/icons-material/Description';
import {
  INVENTORY_COLORS,
  CONSTRUCTION_STATUS_OPTIONS,
  CATEGORY_OPTIONS,
} from '../../../../../utils/inventory_helper';
import InventoryKeyHighlighter from '../../../../../components/inventory/InventoryKeyHighlighter';
import PriceRangeSlider, { DEFAULT_PRICE_MAX } from '../../PriceRangeSlider';

const AREA_UNIT_OPTIONS = [
  { value: 'sqft', label: 'sqft' },
  { value: 'sqmt', label: 'sq.mt' },
  { value: 'acres', label: 'Acres' },
];

/** Convert slug (e.g. near_possession, total_units) to title case for display. */
function slugToTitleCase(s) {
  if (s == null || typeof s !== 'string') return '';
  return s
    .trim()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim() || s;
}

/** Format a single metadata value item for display (avoid [object Object]). */
function formatMetadataItem(item) {
  if (item == null) return '—';
  if (typeof item === 'object') return JSON.stringify(item);
  return String(item);
}

function formatINR(amount) {
  if (amount == null || isNaN(amount)) return '—';
  if (amount >= 1e7) return `₹${(amount / 1e7).toFixed(2).replace(/\.?0+$/, '')} Cr`;
  if (amount >= 1e5) return `₹${(amount / 1e5).toFixed(2).replace(/\.?0+$/, '')} L`;
  return `₹${Number(amount).toLocaleString('en-IN')}`;
}

function formatCommissionOneDecimal(rate) {
  if (rate == null || rate === '' || isNaN(Number(rate))) return '—';
  const n = Number(rate);
  return `${n % 1 === 0 ? n : n.toFixed(1)}%`;
}

// Card header with Edit / Save / Cancel (matches project Overview)
function CardHeader({
  title,
  onEdit,
  onSave,
  onCancel,
  isEditing,
  editLabel = 'Edit',
  saveLabel = 'Save',
  cancelLabel = 'Cancel',
  editStyle = 'grey',
  icon,
  saveDisabled,
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 2.75,
        py: 1.75,
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {icon}
        <Typography
          variant="overline"
          fontWeight={700}
          sx={{ letterSpacing: 1, fontSize: '0.6875rem', color: 'text.secondary' }}
        >
          {title}
        </Typography>
      </Box>
      {isEditing ? (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
          {onCancel && (
            <Box
              component="button"
              type="button"
              onClick={onCancel}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                px: 1.25,
                py: 0.5,
                borderRadius: 2.5,
                border: '1px solid',
                borderColor: 'grey.300',
                fontSize: '0.75rem',
                fontWeight: 600,
                cursor: 'pointer',
                bgcolor: 'transparent',
                color: 'text.secondary',
                '&:hover': { bgcolor: 'grey.100' },
              }}
            >
              {cancelLabel}
            </Box>
          )}
          {onSave && (
            <Box
              component="button"
              type="button"
              onClick={onSave}
              disabled={saveDisabled}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                px: 1.25,
                py: 0.5,
                borderRadius: 2.5,
                border: 'none',
                fontSize: '0.75rem',
                fontWeight: 600,
                cursor: saveDisabled ? 'not-allowed' : 'pointer',
                bgcolor: INVENTORY_COLORS.green,
                color: '#fff',
                opacity: saveDisabled ? 0.7 : 1,
                '&:hover': saveDisabled ? {} : { bgcolor: INVENTORY_COLORS.greenMid },
              }}
            >
              {saveLabel}
            </Box>
          )}
        </Box>
      ) : (
        onEdit && (
          <Box
            component="button"
            type="button"
            onClick={onEdit}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              px: 1.25,
              py: 0.5,
              borderRadius: 2.5,
              border: '1px solid',
              fontSize: '0.75rem',
              fontWeight: 600,
              cursor: 'pointer',
              borderColor: 'grey.300',
              bgcolor: 'grey.100',
              color: 'text.primary',
              '&:hover': { bgcolor: 'grey.200' },
            }}
          >
            <EditIcon sx={{ fontSize: 14 }} />
            {editLabel}
          </Box>
        )
      )}
    </Box>
  );
}

const detailFieldStyles = {
  '& .MuiInputBase-root': { height: 40, fontSize: 12, borderRadius: 1 },
  '& .MuiInputLabel-root': { fontSize: 12 },
};

const ConfigurationOverview = ({
  config,
  project,
  areaUnitOptions = [],
  categoryOptions = [],
  propertyTypeOptions = [],
  isDetailsEditing,
  onEditDetails,
  onSaveDetails,
  onCancelDetails,
  detailsSaving,
  isKeyHighlightsEditing,
  onEditKeyHighlights,
  onSaveKeyHighlights,
  onCancelKeyHighlights,
  keyHighlightsSaving,
  isTagsEditing,
  onEditTags,
  onSaveTags,
  onCancelTags,
  tagsSaving,
  isOtherDetailsEditing,
  onEditOtherDetails,
  onSaveOtherDetails,
  onCancelOtherDetails,
  otherDetailsSaving,
  isCommissionEditing,
  onEditCommission,
  onSaveCommission,
  onCancelCommission,
  commissionSaving,
}) => {
  const areaUnitOpts = areaUnitOptions?.length ? areaUnitOptions : AREA_UNIT_OPTIONS;
  const categoryOpts = categoryOptions?.length ? categoryOptions : CATEGORY_OPTIONS;
  const propertyTypeOpts = propertyTypeOptions ?? [];
  const meta = config?.metadata ?? {};

  const [budgetMin, setBudgetMin] = useState('');
  const [budgetMax, setBudgetMax] = useState('');
  const [areaMin, setAreaMin] = useState('');
  const [areaMax, setAreaMax] = useState('');
  const [areaUnit, setAreaUnit] = useState('sqft');
  const [constructionStatusDraft, setConstructionStatusDraft] = useState('under_construction');
  const [nameDraft, setNameDraft] = useState('');
  const [categoryDraft, setCategoryDraft] = useState('');
  const [propertyTypeDraft, setPropertyTypeDraft] = useState('');
  const [tagsDraft, setTagsDraft] = useState([]);
  const [newTagInput, setNewTagInput] = useState('');
  const [commissionDraft, setCommissionDraft] = useState('');
  const [keyHighlightsDraft, setKeyHighlightsDraft] = useState(['']);
  const [metadataDraft, setMetadataDraft] = useState({});
  const [metadataEditRows, setMetadataEditRows] = useState([]);
  const prevKeyHighlightsEditingRef = useRef(false);
  const prevTagsEditingRef = useRef(false);
  const prevOtherDetailsEditingRef = useRef(false);

  useEffect(() => {
    if (!config?.id) return;
    setMetadataDraft(typeof meta === 'object' && meta !== null ? { ...meta } : {});
  }, [config?.id, config?.metadata]);

  useEffect(() => {
    const justEnteredEdit = isOtherDetailsEditing && !prevOtherDetailsEditingRef.current;
    prevOtherDetailsEditingRef.current = isOtherDetailsEditing;
    if (justEnteredEdit && config) {
      const m = config?.metadata ?? {};
      const entries = Object.entries(typeof m === 'object' && m !== null ? m : {});
      const rows = entries.length > 0
        ? entries.map(([k, v]) => ({ key: k, valueStr: typeof v === 'object' && v !== null ? JSON.stringify(v) : String(v ?? '') }))
        : [{ key: '', valueStr: '' }];
      setMetadataEditRows(rows);
    }
  }, [isOtherDetailsEditing, config?.id, config?.metadata]);

  useEffect(() => {
    const justEnteredEdit = isKeyHighlightsEditing && !prevKeyHighlightsEditingRef.current;
    prevKeyHighlightsEditingRef.current = isKeyHighlightsEditing;
    if (justEnteredEdit && config) {
      const raw = Array.isArray(config.key_highlights) ? config.key_highlights : [];
      setKeyHighlightsDraft(raw.length > 0 ? raw.map((h) => String(h).trim()) : ['']);
    }
  }, [isKeyHighlightsEditing, config?.id, config?.key_highlights]);

  useEffect(() => {
    const justEnteredEdit = isTagsEditing && !prevTagsEditingRef.current;
    prevTagsEditingRef.current = isTagsEditing;
    if (justEnteredEdit && config) {
      const tags = config?.tags ?? config?.metadata?.tags ?? [];
      setTagsDraft(Array.isArray(tags) ? [...tags] : []);
      setNewTagInput('');
    }
  }, [isTagsEditing, config?.id, config?.tags]);

  useEffect(() => {
    if (isDetailsEditing && config) {
      setBudgetMin(config.price_min != null ? String(config.price_min) : '');
      setBudgetMax(config.price_max != null ? String(config.price_max) : '');
      setAreaMin(config.area_min != null ? String(config.area_min) : '');
      setAreaMax(config.area_max != null ? String(config.area_max) : '');
      setAreaUnit(config.area_unit || 'sqft');
      setConstructionStatusDraft(config.construction_status || 'under_construction');
      setNameDraft(config.name ?? '');
      const ptId = config.property_type_id ?? (typeof config.property_type === 'object' ? config.property_type?.id : config.property_type);
      setPropertyTypeDraft(ptId != null ? String(ptId) : '');
      const pt = (propertyTypeOpts || []).find((p) => p.id === ptId || p.id === Number(ptId));
      const catId = pt?.category;
      setCategoryDraft(catId != null ? String(catId) : (config.category_display ?? config.category ?? ''));
    }
  }, [isDetailsEditing, config?.id, config?.price_min, config?.price_max, config?.area_min, config?.area_max, config?.area_unit, config?.construction_status, config?.name, config?.property_type, config?.property_type_id, config?.category_display, config?.category, propertyTypeOpts]);

  useEffect(() => {
    if (isCommissionEditing) {
      const rate = config?.commission ?? config?.metadata?.commission_rate;
      setCommissionDraft(rate != null && rate !== '' ? String(rate) : '');
    }
  }, [isCommissionEditing, config?.id]);

  const handleSaveDetails = () => {
    const ptId = propertyTypeDraft.trim() ? (Number(propertyTypeDraft) || propertyTypeDraft) : null;
    const payload = {
      name: (nameDraft ?? '').trim() || undefined,
      construction_status: constructionStatusDraft || 'under_construction',
      price_min: budgetMin.trim() ? parseFloat(budgetMin) : null,
      price_max: budgetMax.trim() ? parseFloat(budgetMax) : null,
      area_min: areaMin.trim() ? parseFloat(areaMin) : null,
      area_max: areaMax.trim() ? parseFloat(areaMax) : null,
      area_unit: areaUnit || null,
      property_type: ptId,
      category: categoryDraft || undefined,
    };
    onSaveDetails?.(payload);
  };

  /** Parse metadata value: string, number, list (JSON array), or object (JSON object). */
  function parseMetadataValue(str) {
    const s = typeof str === 'string' ? str.trim() : '';
    if (s === '') return '';
    if (/^\[/.test(s) || /^\{/.test(s)) {
      try { return JSON.parse(s); } catch (_) { return s; }
    }
    const n = Number(s);
    if (!Number.isNaN(n) && s !== '') return n;
    return s;
  }

  const handleSaveOtherDetails = () => {
    const out = {};
    (metadataEditRows || []).forEach((row) => {
      const k = String(row?.key ?? '').trim();
      if (!k) return;
      out[k] = parseMetadataValue(row?.valueStr ?? '');
    });
    setMetadataDraft(out);
    onSaveOtherDetails?.(out);
  };

  const addMetadataRow = () => setMetadataEditRows((prev) => [...(prev || []), { key: '', valueStr: '' }]);
  const updateMetadataRow = (index, field, value) => {
    setMetadataEditRows((prev) => {
      const next = [...(prev || [])];
      if (!next[index]) next[index] = { key: '', valueStr: '' };
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };
  const removeMetadataRow = (index) => {
    setMetadataEditRows((prev) => {
      const next = prev.filter((_, i) => i !== index);
      return next.length ? next : [{ key: '', valueStr: '' }];
    });
  };

  const handleSaveKeyHighlights = () => {
    const list = (keyHighlightsDraft || []).map((h) => String(h).trim()).filter(Boolean);
    onSaveKeyHighlights?.(list);
  };

  const handleSaveTags = () => {
    onSaveTags?.([...new Set(tagsDraft.map((t) => String(t).trim().toLowerCase()).filter(Boolean))]);
  };

  const addTag = () => {
    const t = newTagInput.trim().toLowerCase();
    if (t && !tagsDraft.includes(t)) setTagsDraft((prev) => [...prev, t]);
    setNewTagInput('');
  };

  const removeTag = (index) => {
    setTagsDraft((prev) => prev.filter((_, i) => i !== index));
  };

  const isValidCommission = () => {
    if (commissionDraft === '') return true;
    const n = parseFloat(commissionDraft);
    return !isNaN(n) && n >= 0 && n <= 100;
  };

  const handleSaveCommission = () => {
    if (!isValidCommission()) return;
    onSaveCommission?.(commissionDraft);
  };

  const projectDefaultRate = project?.commissionDefault ?? project?.commission ?? '—';
  const hasConfigCommission = config?.commission != null && config.commission !== '';
  const displayRate = hasConfigCommission ? formatCommissionOneDecimal(config.commission) : projectDefaultRate;
  const commissionLabel = hasConfigCommission ? 'Configuration commission' : 'Unit default commission';
  const commissionSubtitle = hasConfigCommission
    ? 'Custom commission for this configuration'
    : 'Applied to all units unless overridden';

  const tags = config?.tags ?? [];
  const unitTypeDisplay = config?.property_type_name ?? (typeof config?.property_type === 'object' ? config?.property_type?.name : config?.property_type) ?? '—';
  const categoryDisplay = config?.category_display ?? config?.category ?? '—';
  const constructionStatusDisplay = CONSTRUCTION_STATUS_OPTIONS.find((o) => o.value === (config?.construction_status ?? constructionStatusDraft))?.label ?? config?.construction_status_display ?? config?.construction_status ?? '—';

  const chipSx = {
    fontSize: '0.75rem',
    fontWeight: 500,
    py: 0.6,
    borderRadius: 2.5,
    bgcolor: INVENTORY_COLORS.greenLight,
    color: INVENTORY_COLORS.green,
    border: '1px solid',
    borderColor: INVENTORY_COLORS.greenAccent + '99',
    '& .MuiChip-label': { px: 1.5 },
  };

  const filteredPropertyTypes = !categoryDraft
    ? propertyTypeOpts
    : propertyTypeOpts.filter((pt) => {
        const cat = pt.category;
        const catName = (pt.category_name ?? '').toString().toLowerCase();
        const draft = String(categoryDraft || '').toLowerCase();
        if (Number(categoryDraft) === Number(cat)) return true;
        if (draft && catName === draft) return true;
        if (draft && (pt.category_slug ?? pt.category_value ?? '').toString().toLowerCase() === draft) return true;
        return false;
      });

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '3fr 2fr' },
        gap: 2.5,
        alignItems: 'start',
      }}
    >
      {/* Left: Configuration details, Tags, Other Details */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        {/* ── 1. Configuration details ───────────────────────────────────── */}
        <Card variant="outlined" sx={{ borderRadius: 2.5, overflow: 'hidden' }}>
          <CardHeader
            title="Configuration details"
            onEdit={onEditDetails}
            onSave={isDetailsEditing ? handleSaveDetails : undefined}
            onCancel={isDetailsEditing ? onCancelDetails : undefined}
            isEditing={isDetailsEditing}
            editLabel="Edit"
            saveLabel="Save"
            cancelLabel="Cancel"
            saveDisabled={detailsSaving}
            editStyle="grey"
            icon={
              <Box sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: INVENTORY_COLORS.greenLight, border: '1px solid', borderColor: INVENTORY_COLORS.greenAccent + 'cc', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <ViewModuleIcon sx={{ fontSize: 16, color: INVENTORY_COLORS.green }} />
              </Box>
            }
          />
          <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {!isDetailsEditing && (
              <Box sx={{ mb: 0.5, p: 1.5, borderRadius: 1.5, bgcolor: INVENTORY_COLORS.greenLight, border: '1px solid', borderColor: INVENTORY_COLORS.greenAccent + '99', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, borderRadius: '50%', bgcolor: INVENTORY_COLORS.green + '18', flexShrink: 0 }}>
                  <CheckCircleIcon sx={{ fontSize: 17, color: INVENTORY_COLORS.green }} />
                </Box>
                <Box>
                  <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary', fontWeight: 600, lineHeight: 1, mb: 0.4 }}>Construction Status</Typography>
                  <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, color: INVENTORY_COLORS.green }}>{constructionStatusDisplay}</Typography>
                </Box>
              </Box>
            )}

            {/* Section 1: Basic details — Name, Construction Status | Category, Property Type */}
            <Box>
              <Typography variant="overline" sx={{ fontSize: '0.6875rem', fontWeight: 700, color: 'text.secondary', letterSpacing: 1 }}>Basic details</Typography>
              {isDetailsEditing ? (
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mt: 1 }}>
                  <Box>
                    <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary', fontWeight: 600, mb: 0.5 }}>Name</Typography>
                    <TextField fullWidth size="small" value={nameDraft} onChange={(e) => setNameDraft(e.target.value)} placeholder="Configuration name" sx={detailFieldStyles} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary', fontWeight: 600, mb: 0.5 }}>Construction Status</Typography>
                    <FormControl fullWidth size="small" sx={detailFieldStyles}>
                      <Select value={constructionStatusDraft} onChange={(e) => setConstructionStatusDraft(e.target.value)} displayEmpty>
                        {CONSTRUCTION_STATUS_OPTIONS.map((opt) => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary', fontWeight: 600, mb: 0.5 }}>Category</Typography>
                    <FormControl fullWidth size="small" sx={detailFieldStyles}>
                      <Select value={categoryDraft} onChange={(e) => { setCategoryDraft(e.target.value); setPropertyTypeDraft(''); }} displayEmpty renderValue={(v) => (categoryOpts.find((o) => (o.id != null ? String(o.id) : o.value) === v)?.label ?? categoryOpts.find((o) => o.value === v)?.label ?? v ?? '')}>
                        <MenuItem value="">—</MenuItem>
                        {categoryOpts.map((opt) => (
                          <MenuItem key={opt.id ?? opt.value} value={opt.id != null ? String(opt.id) : opt.value}>{opt.label ?? opt.name ?? opt.value}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary', fontWeight: 600, mb: 0.5 }}>Property Type</Typography>
                    <FormControl fullWidth size="small" sx={detailFieldStyles}>
                      <Select value={propertyTypeDraft} onChange={(e) => setPropertyTypeDraft(e.target.value)} displayEmpty renderValue={(v) => (filteredPropertyTypes.find((p) => String(p.id) === v)?.name ?? v ?? '')}>
                        <MenuItem value="">—</MenuItem>
                        {filteredPropertyTypes.map((pt) => (
                          <MenuItem key={pt.id} value={String(pt.id)}>{pt.name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                </Box>
              ) : (
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.25, mt: 1 }}>
                  <Box sx={{ p: 1.25, borderRadius: 1.25, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
                    <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary', fontWeight: 600, mb: 0.25 }}>Name</Typography>
                    <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600 }}>{config?.name ?? '—'}</Typography>
                  </Box>
                  <Box sx={{ p: 1.25, borderRadius: 1.25, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
                    <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary', fontWeight: 600, mb: 0.25 }}>Construction Status</Typography>
                    <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, color: INVENTORY_COLORS.green }}>{constructionStatusDisplay}</Typography>
                  </Box>
                  <Box sx={{ p: 1.25, borderRadius: 1.25, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
                    <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary', fontWeight: 600, mb: 0.25 }}>Category</Typography>
                    <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600 }}>{categoryDisplay}</Typography>
                  </Box>
                  <Box sx={{ p: 1.25, borderRadius: 1.25, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
                    <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary', fontWeight: 600, mb: 0.25 }}>Property Type</Typography>
                    <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600 }}>{unitTypeDisplay}</Typography>
                  </Box>
                </Box>
              )}
            </Box>

            {/* Section 2: Price & Area range */}
            <Box sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 2 }}>
              <Typography variant="overline" sx={{ fontSize: '0.6875rem', fontWeight: 700, color: 'text.secondary', letterSpacing: 1 }}>Price & Area range</Typography>
              {isDetailsEditing ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                  <Box>
                    <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary', fontWeight: 600, mb: 0.75 }}>Price range</Typography>
                    <PriceRangeSlider
                      value={[Number(budgetMin) || 0, Number(budgetMax) || DEFAULT_PRICE_MAX]}
                      onChange={(v) => { setBudgetMin(String(v[0])); setBudgetMax(String(v[1])); }}
                    />
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary', fontWeight: 600, mb: 0.5 }}>Area range</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                      <TextField size="small" type="number" value={areaMin} onChange={(e) => setAreaMin(e.target.value)} placeholder="Min" inputProps={{ min: 0 }} sx={{ ...detailFieldStyles, width: 100 }} />
                      <TextField size="small" type="number" value={areaMax} onChange={(e) => setAreaMax(e.target.value)} placeholder="Max" inputProps={{ min: 0 }} sx={{ ...detailFieldStyles, width: 100 }} />
                      <FormControl size="small" sx={{ ...detailFieldStyles, minWidth: 100 }}>
                        <Select value={areaUnit} onChange={(e) => setAreaUnit(e.target.value)} displayEmpty renderValue={(v) => areaUnitOpts.find((o) => o.value === v)?.label ?? v}>
                          {areaUnitOpts.map((opt) => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
                        </Select>
                      </FormControl>
                    </Box>
                  </Box>
                </Box>
              ) : (
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.25, mt: 1 }}>
                  <Box sx={{ p: 1.25, borderRadius: 1.25, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
                    <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary', fontWeight: 600, mb: 0.25 }}>Price range</Typography>
                    <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, color: INVENTORY_COLORS.green }}>{config?.priceRange ?? '—'}</Typography>
                  </Box>
                  <Box sx={{ p: 1.25, borderRadius: 1.25, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
                    <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary', fontWeight: 600, mb: 0.25 }}>Area range</Typography>
                    <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600 }}>{config?.areaRange ?? '—'}</Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </Card>

        {/* ── 2. Tags ── */}
        <Card variant="outlined" sx={{ borderRadius: 2.5, overflow: 'hidden' }}>
          <CardHeader
            title="Tags"
            onEdit={onEditTags}
            onSave={isTagsEditing ? handleSaveTags : undefined}
            onCancel={isTagsEditing ? onCancelTags : undefined}
            isEditing={isTagsEditing}
            saveDisabled={tagsSaving}
            editStyle="grey"
            icon={<LayersIcon sx={{ fontSize: 18, color: INVENTORY_COLORS.green }} />}
          />
          <Box sx={{ p: 2 }}>
            {isTagsEditing ? (
              <>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
                  {tagsDraft.map((tag, index) => (
                    <Chip key={`${tag}-${index}`} label={tag} size="small" onDelete={() => removeTag(index)} sx={{ ...chipSx, '& .MuiChip-deleteIcon': { fontSize: 16 } }} />
                  ))}
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                  <TextField size="small" placeholder="New tag (saved as lowercase)" value={newTagInput} onChange={(e) => setNewTagInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }} sx={{ ...detailFieldStyles, flex: '1 1 120px', minWidth: 140, '& .MuiInputBase-root': { height: 36 } }} />
                  <Box component="button" type="button" onClick={addTag} disabled={!newTagInput.trim()} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1.25, py: 0.75, borderRadius: 1.25, border: 'none', fontSize: '0.8125rem', fontWeight: 600, cursor: newTagInput.trim() ? 'pointer' : 'not-allowed', bgcolor: INVENTORY_COLORS.green, color: '#fff', opacity: newTagInput.trim() ? 1 : 0.6, '&:hover': newTagInput.trim() ? { bgcolor: INVENTORY_COLORS.greenMid } : {} }}><AddIcon sx={{ fontSize: 18 }} /> Add</Box>
                </Box>
                <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>Tags are stored as lowercase and unique. Press Enter or click Add to add.</Typography>
              </>
            ) : (
              <>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {tags?.length > 0 ? tags.map((t) => <Chip key={t} label={t} size="small" sx={chipSx} />) : <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>No tags added.</Typography>}
                </Box>
                {onEditTags && (
                  <Typography component="span" onClick={onEditTags} sx={{ mt: 1.25, display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: '0.8125rem', color: INVENTORY_COLORS.green, cursor: 'pointer', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}>
                    <AddIcon sx={{ fontSize: 18 }} /> Add Tag
                  </Typography>
                )}
              </>
            )}
          </Box>
        </Card>

        {/* ── 3. Other Details — metadata as key:value (string, list, or object) ── */}
        <Card variant="outlined" sx={{ borderRadius: 2.5, overflow: 'hidden' }}>
          <CardHeader
            title="Other Details"
            onEdit={onEditOtherDetails}
            onSave={isOtherDetailsEditing ? handleSaveOtherDetails : undefined}
            onCancel={isOtherDetailsEditing ? onCancelOtherDetails : undefined}
            isEditing={isOtherDetailsEditing}
            saveDisabled={otherDetailsSaving}
            editStyle="grey"
            icon={<DescriptionIcon sx={{ fontSize: 18, color: INVENTORY_COLORS.green }} />}
          />
          <Box sx={{ p: 2 }}>
            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 1 }}>Add custom key–value pairs.</Typography>
            {isOtherDetailsEditing ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                {(metadataEditRows || []).map((row, index) => (
                  <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                    <TextField size="small" placeholder="Key" value={row?.key ?? ''} onChange={(e) => updateMetadataRow(index, 'key', e.target.value)} sx={{ ...detailFieldStyles, minWidth: 140 }} />
                    <TextField size="small" placeholder='Value: string, number, list [1,2], or object {"a":1}' value={row?.valueStr ?? ''} onChange={(e) => updateMetadataRow(index, 'valueStr', e.target.value)} sx={{ ...detailFieldStyles, flex: 1, minWidth: 160 }} />
                    <IconButton size="small" onClick={() => removeMetadataRow(index)} disabled={(metadataEditRows || []).length <= 1} sx={{ color: 'text.secondary' }}><DeleteOutlineIcon fontSize="small" /></IconButton>
                  </Box>
                ))}
                <Box component="button" type="button" onClick={addMetadataRow} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: '0.75rem', fontWeight: 600, color: INVENTORY_COLORS.green, cursor: 'pointer', border: 'none', bgcolor: 'transparent', '&:hover': { textDecoration: 'underline' } }}><AddIcon sx={{ fontSize: 18 }} /> Add row</Box>
              </Box>
            ) : (
              <Box sx={{ mt: 1 }}>
                {Object.keys(meta || {}).length > 0 ? Object.entries(meta || {}).map(([k, v]) => (
                  <Box key={k} sx={{ py: 0.5, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                    <Typography variant="body2" color="text.secondary">{slugToTitleCase(k)}</Typography>
                    <Typography variant="body2" component="span" sx={{ textAlign: 'right', display: 'block' }}>
                      {Array.isArray(v)
                        ? (
                            <Box component="span" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.25 }}>
                              {v.map((item, i) => (
                                <span key={i}>{formatMetadataItem(item)}</span>
                              ))}
                            </Box>
                          )
                        : (typeof v === 'object' && v !== null ? JSON.stringify(v) : String(v ?? '—'))}
                    </Typography>
                  </Box>
                )) : <Typography variant="body2" color="text.secondary">No other details.</Typography>}
              </Box>
            )}
          </Box>
        </Card>
      </Box>

      {/* Right: Commission */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <Card variant="outlined" sx={{ borderRadius: 2.5, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
          <CardHeader
            title="Commission Structure"
            onEdit={onEditCommission}
            onSave={isCommissionEditing ? handleSaveCommission : undefined}
            onCancel={isCommissionEditing ? onCancelCommission : undefined}
            isEditing={isCommissionEditing}
            editLabel="Edit"
            saveLabel="Save"
            cancelLabel="Cancel"
            saveDisabled={commissionSaving || (isCommissionEditing && !isValidCommission())}
            editStyle="grey"
            icon={<CurrencyRupeeIcon sx={{ fontSize: 18, color: INVENTORY_COLORS.green }} />}
          />

          {/* Configuration rate — hero box (same layout as project) */}
          <Box
            sx={{
              mx: 2.75,
              mt: 2,
              mb: 0,
              p: 2.5,
              borderRadius: 1.5,
              background: `linear-gradient(135deg, ${INVENTORY_COLORS.green} 0%, ${INVENTORY_COLORS.greenMid} 100%)`,
              textAlign: 'center',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <Box sx={{ position: 'absolute', top: -8, right: -8, width: 64, height: 64, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.08)' }} />
            <Typography sx={{ fontSize: '0.625rem', fontWeight: 600, letterSpacing: 2, textTransform: 'uppercase', color: 'rgba(255,255,255,0.8)', display: 'block', mb: 1 }}>
              {commissionLabel}
            </Typography>
            {isCommissionEditing ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                <TextField
                  type="number"
                  value={commissionDraft}
                  onChange={(e) => setCommissionDraft(e.target.value)}
                  inputProps={{ min: 0, max: 100, step: 0.01 }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                    sx: { bgcolor: 'rgba(255,255,255,0.95)', borderRadius: 1, maxWidth: 140 },
                  }}
                  size="small"
                  placeholder="0–100"
                  error={commissionDraft !== '' && !isValidCommission()}
                  helperText={commissionDraft !== '' && !isValidCommission() ? 'Enter 0–100' : ''}
                  sx={{
                    '& .MuiInputBase-root': { color: 'grey.900' },
                    '& .MuiFormHelperText-root': { color: 'rgba(255,255,255,0.9)' },
                  }}
                />
                <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.75)' }}>
                  Percentage (0–100), e.g. 2.5
                </Typography>
              </Box>
            ) : (
              <>
                <Typography sx={{ fontFamily: 'Georgia, serif', fontSize: '3.25rem', fontWeight: 700, color: '#fff', lineHeight: 1, letterSpacing: '-1px' }}>
                  {displayRate}
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.75)', mt: 1 }}>
                  {commissionSubtitle}
                </Typography>
              </>
            )}
          </Box>

          {/* Unit Default and This configuration rows */}
          <Box sx={{ px: 2.75, py: 2, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.5, borderRadius: 1.25, bgcolor: INVENTORY_COLORS.greenLight, border: '1px solid', borderColor: INVENTORY_COLORS.greenAccent + '80' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: INVENTORY_COLORS.greenAccent, flexShrink: 0 }} />
                <Box>
                  <Typography variant="body2" fontWeight={700} color="text.primary">Default Unit Commission</Typography>
                  <Typography variant="caption" color="text.secondary">Applied to all the units in this configuration unless overridden</Typography>
                </Box>
              </Box>
              <Typography variant="body1" fontWeight={700} sx={{ color: INVENTORY_COLORS.green }}>
                {hasConfigCommission
                  ? formatCommissionOneDecimal(config?.commission)
                  : formatCommissionOneDecimal(
                      project?.commission_rate ?? (typeof project?.commission === 'string' ? parseFloat(project.commission) : project?.commission) ?? null
                    )}
              </Typography>
            </Box>
          </Box>

          {/* Info message — only when configuration-level commission not set */}
          {!hasConfigCommission && (
            <Box sx={{ mx: 2.75, mb: 2, p: 1.5, borderRadius: 1.25, bgcolor: '#fffbeb', border: '1px solid rgba(245,158,11,0.3)', display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <WarningAmberIcon sx={{ fontSize: 20, color: '#ea580c', flexShrink: 0, mt: 0.25 }} />
              <Typography variant="caption" sx={{ color: '#92400e', lineHeight: 1.6 }}>
                Set a custom commission above to override the project's default commission for this configuration only.
              </Typography>
            </Box>
          )}
        </Card>

        {/* Key Highlights — below Commission in right column */}
        <Card variant="outlined" sx={{ borderRadius: 2.5, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
          <CardHeader
            title="Key Highlights"
            onEdit={onEditKeyHighlights}
            onSave={isKeyHighlightsEditing ? handleSaveKeyHighlights : undefined}
            onCancel={isKeyHighlightsEditing ? onCancelKeyHighlights : undefined}
            isEditing={isKeyHighlightsEditing}
            saveDisabled={keyHighlightsSaving}
            editStyle="grey"
            icon={<LayersIcon sx={{ fontSize: 18, color: INVENTORY_COLORS.green }} />}
          />
          <Box sx={{ p: 2 }}>
            {isKeyHighlightsEditing ? (
              <InventoryKeyHighlighter value={keyHighlightsDraft?.length ? keyHighlightsDraft : ['']} onChange={setKeyHighlightsDraft} label="Key highlights" placeholder="e.g. Prime location, 24/7 security" sx={{ mt: 0 }} />
            ) : (
              <>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {Array.isArray(config?.key_highlights) && config.key_highlights.length > 0
                    ? config.key_highlights.map((h, i) => <Chip key={i} label={h} size="small" sx={chipSx} />)
                    : <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>No key highlights added.</Typography>}
                </Box>
                {onEditKeyHighlights && (
                  <Typography
                    component="span"
                    onClick={onEditKeyHighlights}
                    sx={{ mt: 1.25, display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: '0.8125rem', color: INVENTORY_COLORS.green, cursor: 'pointer', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}
                  >
                    <AddIcon sx={{ fontSize: 18 }} /> Add Key Highlight
                  </Typography>
                )}
              </>
            )}
          </Box>
        </Card>
      </Box>
    </Box>
  );
};

export default ConfigurationOverview;
